import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Card,
  Col,
  Container,
  Row,
  Spinner,
} from "react-bootstrap";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);

const GITHUB_OWNER = process.env.REACT_APP_GITHUB_OWNER || "Tanveerfb";
const GITHUB_REPO = process.env.REACT_APP_GITHUB_REPO || "ncafirebase";
const GITHUB_BRANCH = process.env.REACT_APP_GITHUB_BRANCH || "master";
const GITHUB_TOKEN = "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";
const COMMITS_PER_PAGE_RAW = Number.parseInt(
  process.env.REACT_APP_COMMITS_PAGE_SIZE || "10",
  10
);
const COMMITS_DETAILS_LIMIT_RAW = Number.parseInt(
  process.env.REACT_APP_COMMITS_DETAILS_LIMIT || "10",
  10
);
const COMMITS_PER_PAGE = Number.isFinite(COMMITS_PER_PAGE_RAW)
  ? Math.max(1, Math.min(COMMITS_PER_PAGE_RAW, 100))
  : 30;
const DETAILS_LIMIT = Number.isFinite(COMMITS_DETAILS_LIMIT_RAW)
  ? Math.max(1, Math.min(COMMITS_DETAILS_LIMIT_RAW, 50))
  : 10;
const COMMITS_ENDPOINT = process.env.REACT_APP_COMMITS_ENDPOINT || "";
const USE_PROXY = Boolean(COMMITS_ENDPOINT);

const buildCommitsEndpoint = () =>
  `https://api.github.com/repos/${GITHUB_OWNER}/${GITHUB_REPO}/commits?sha=${GITHUB_BRANCH}&per_page=${COMMITS_PER_PAGE}`;

const buildCommitDetailsEndpoint = (sha) =>
  `https://api.github.com/repos/${GITHUB_OWNER}/${GITHUB_REPO}/commits/${sha}`;

const buildProxyEndpoint = () => {
  if (!COMMITS_ENDPOINT) {
    return null;
  }

  try {
    const isAbsolute = /^https?:/i.test(COMMITS_ENDPOINT);
    const baseUrl = isAbsolute
      ? new URL(COMMITS_ENDPOINT)
      : new URL(
          COMMITS_ENDPOINT,
          typeof window !== "undefined"
            ? window.location.origin
            : "http://localhost"
        );

    baseUrl.searchParams.set("owner", GITHUB_OWNER);
    baseUrl.searchParams.set("repo", GITHUB_REPO);
    baseUrl.searchParams.set("branch", GITHUB_BRANCH);
    baseUrl.searchParams.set("per_page", String(COMMITS_PER_PAGE));
    baseUrl.searchParams.set("include_stats", "true");

    return baseUrl.toString();
  } catch (error) {
    console.error("Invalid REACT_APP_COMMITS_ENDPOINT", error);
    return null;
  }
};

const apiHeaders = () => {
  const headers = {
    Accept: "application/vnd.github+json",
    "X-GitHub-Api-Version": "2022-11-28",
    "User-Agent": "NCA-Portal-Updates",
  };
  if (GITHUB_TOKEN) {
    headers.Authorization = `Bearer ${GITHUB_TOKEN}`;
  }
  return headers;
};

const parseErrorResponse = async (response) => {
  let apiMessage = "";
  try {
    const json = await response.json();
    apiMessage = json?.message || json?.error || "";
  } catch (jsonError) {
    try {
      const text = await response.text();
      apiMessage = text?.slice(0, 240) || "";
    } catch (textError) {
      apiMessage = "";
    }
  }

  let message;
  if (response.status === 401) {
    message = USE_PROXY
      ? "Proxy rejected the request (401). Ensure the backend allows your origin and the token is valid."
      : "GitHub rejected the request (401 Unauthorized). Check that REACT_APP_GITHUB_TOKEN is valid and has repo read access.";
  } else if (response.status === 403) {
    message = USE_PROXY
      ? "Proxy reports a permission issue (403). Confirm the backend token has repo:read scope or rate limits are not exceeded."
      : "GitHub rate limit reached or token lacks scopes (403). Try again later or use a personal access token with repo:read scope.";
  } else if (response.status === 404) {
    message = USE_PROXY
      ? "Proxy cannot find the repository or branch (404). Verify owner/repo/branch on the backend."
      : "GitHub reports the repository or branch was not found (404). Ensure the repo is accessible and the branch name is correct.";
  } else if (response.status === 429) {
    message =
      "Request throttled (429). Please wait a moment before refreshing.";
  } else {
    message = `Unable to load recent commits (status ${response.status}).`;
  }

  if (apiMessage) {
    message = `${message}\nMessage: ${apiMessage}`;
  }

  return message;
};

const formatDate = (value, options) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) {
    return "Unknown";
  }
  return new Intl.DateTimeFormat("en-AU", options).format(date);
};

const formatShortDate = (value) =>
  formatDate(value, { month: "short", day: "numeric" });

const formatLongDate = (value) =>
  formatDate(value, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

export default function Updates() {
  const [commits, setCommits] = useState([]);
  const [commitDetails, setCommitDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;

    async function loadCommits() {
      setLoading(true);
      setError(null);

      try {
        const endpoint = USE_PROXY
          ? buildProxyEndpoint()
          : buildCommitsEndpoint();

        if (!endpoint) {
          throw new Error(
            "Commit endpoint is not configured. Set REACT_APP_COMMITS_ENDPOINT for proxy mode or provide GitHub owner/repo/branch values."
          );
        }

        const options = USE_PROXY ? {} : { headers: apiHeaders() };
        const response = await fetch(endpoint, options);

        if (!response.ok) {
          throw new Error(await parseErrorResponse(response));
        }

        const payload = await response.json();
        if (!active) {
          return;
        }

        if (USE_PROXY) {
          const proxyCommits = Array.isArray(payload?.commits)
            ? payload.commits
            : [];
          setCommits(proxyCommits);
          setCommitDetails(payload?.stats || {});
          return;
        }

        const data = Array.isArray(payload) ? payload : [];
        setCommits(data);

        const detailTargets = data.slice(
          0,
          Number.isFinite(DETAILS_LIMIT) && DETAILS_LIMIT > 0
            ? DETAILS_LIMIT
            : 10
        );

        if (!detailTargets.length) {
          setCommitDetails({});
          return;
        }

        const detailResponses = await Promise.all(
          detailTargets.map(async (commit) => {
            try {
              const detailResponse = await fetch(
                buildCommitDetailsEndpoint(commit.sha),
                { headers: apiHeaders() }
              );
              if (!detailResponse.ok) {
                return null;
              }
              const detailJson = await detailResponse.json();
              if (!detailJson?.sha || !detailJson?.stats) {
                return null;
              }
              return { sha: detailJson.sha, stats: detailJson.stats };
            } catch (detailError) {
              console.warn("Failed to load commit detail", detailError);
              return null;
            }
          })
        );

        if (!active) {
          return;
        }

        const detailsMap = detailResponses.reduce((accumulator, item) => {
          if (item && item.sha && item.stats) {
            accumulator[item.sha] = item.stats;
          }
          return accumulator;
        }, {});

        setCommitDetails(detailsMap);
      } catch (err) {
        if (active) {
          setError(err.message || "Unable to load recent commits.");
          setCommits([]);
          setCommitDetails({});
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadCommits();

    const refreshInterval = setInterval(loadCommits, 5 * 60 * 1000);

    return () => {
      active = false;
      clearInterval(refreshInterval);
    };
  }, []);

  const latestCommit = commits[0] || null;

  const commitActivity = useMemo(() => {
    if (!commits.length) {
      return { labels: [], values: [] };
    }

    const counts = new Map();

    commits.forEach((commit) => {
      const dateKey = (
        commit.commit?.author?.date ||
        commit.commit?.committer?.date ||
        commit.author?.date
      )?.slice(0, 10);
      if (!dateKey) {
        return;
      }
      const current = counts.get(dateKey) || 0;
      counts.set(dateKey, current + 1);
    });

    const sorted = Array.from(counts.entries()).sort((a, b) =>
      a[0].localeCompare(b[0])
    );

    const recent = sorted.slice(-14);

    return {
      labels: recent.map(([date]) => formatShortDate(date)),
      values: recent.map(([, value]) => value),
    };
  }, [commits]);

  const chartData = useMemo(() => {
    if (!commitActivity.labels.length) {
      return null;
    }

    return {
      labels: commitActivity.labels,
      datasets: [
        {
          label: "Commits per day",
          data: commitActivity.values,
          tension: 0.35,
          fill: true,
          backgroundColor: "rgba(0, 173, 217, 0.18)",
          borderColor: "rgba(0, 102, 128, 0.8)",
          borderWidth: 2,
          pointBackgroundColor: "rgba(0, 63, 79, 0.95)",
          pointRadius: 4,
        },
      ],
    };
  }, [commitActivity]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            precision: 0,
            color: "rgba(0, 49, 61, 0.8)",
            font: {
              family: "'Inter', 'Segoe UI', sans-serif",
            },
          },
          grid: {
            color: "rgba(0, 49, 61, 0.08)",
            drawBorder: false,
          },
        },
        x: {
          ticks: {
            color: "rgba(0, 49, 61, 0.6)",
            maxRotation: 0,
            font: {
              family: "'Inter', 'Segoe UI', sans-serif",
            },
          },
          grid: {
            display: false,
          },
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          backgroundColor: "rgba(0, 49, 61, 0.92)",
          padding: 12,
          displayColors: false,
          callbacks: {
            label: (context) =>
              `${context.parsed.y} commit${context.parsed.y === 1 ? "" : "s"}`,
          },
        },
      },
    }),
    []
  );

  const uniqueAuthors = useMemo(() => {
    if (!commits.length) {
      return [];
    }
    const authors = new Map();
    commits.forEach((commit) => {
      const login = commit.author?.login;
      const name = commit.commit?.author?.name || login || "Unknown";
      if (!authors.has(name)) {
        authors.set(name, {
          name,
          count: 0,
          avatar: commit.author?.avatar_url,
        });
      }
      authors.get(name).count += 1;
      if (!authors.get(name).avatar && commit.author?.avatar_url) {
        authors.get(name).avatar = commit.author.avatar_url;
      }
    });
    return Array.from(authors.values()).sort((a, b) => b.count - a.count);
  }, [commits]);

  const visibleCommits = commits.slice(0, 20);

  return (
    <Container fluid className="updates-page py-5">
      <Row className="justify-content-center">
        <Col xl={9} lg={10}>
          <div className="updates-hero mb-4">
            <div className="updates-hero-content">
              <Badge bg="light" className="updates-hero-badge text-dark">
                Release Notes
              </Badge>
              <h1 className="updates-hero-title">Change history</h1>
              {latestCommit && (
                <p className="updates-hero-subtitle">
                  Latest:{" "}
                  <strong>
                    {latestCommit.commit?.message?.split("\n")[0]}
                  </strong>
                </p>
              )}
            </div>
          </div>
          {loading && (
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          )}
          {error && (
            <Alert variant="danger" className="shadow-sm">
              {error}
            </Alert>
          )}
          {!loading && !error && (
            <Row className="g-4">
              <Col lg={5}>
                <Card className="updates-summary-card">
                  <Card.Body>
                    <h2 className="updates-summary-title">Activity</h2>
                    <p className="updates-summary-description">
                      Tracking the last {commits.length} commits on{" "}
                      <strong>
                        {GITHUB_OWNER}/{GITHUB_REPO}
                      </strong>{" "}
                      ({GITHUB_BRANCH}).
                    </p>
                    <div className="updates-chart-wrapper mb-4">
                      {chartData ? (
                        <Line data={chartData} options={chartOptions} />
                      ) : (
                        <div className="updates-chart-empty">
                          Not enough data to chart activity yet.
                        </div>
                      )}
                    </div>
                    <div className="updates-summary-list">
                      <div className="updates-summary-item">
                        <span>Total commits in view</span>
                        <strong>{commits.length}</strong>
                      </div>
                      <div className="updates-summary-item">
                        <span>Unique contributors</span>
                        <strong>{uniqueAuthors.length}</strong>
                      </div>
                      {uniqueAuthors.slice(0, 3).map((author) => (
                        <div key={author.name} className="updates-summary-item">
                          <span>{author.name}</span>
                          <strong>{author.count} commits</strong>
                        </div>
                      ))}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={7}>
                <Card className="updates-card">
                  <Card.Body>
                    <div className="updates-commit-header">
                      <h2 className="updates-commit-title">Recent commits</h2>
                      <p className="updates-commit-subtitle">
                        Showing the {visibleCommits.length} most recent commits.
                        Data refreshes every 5 minutes.
                      </p>
                    </div>
                    <div className="updates-commit-list">
                      {visibleCommits.map((commit) => {
                        const message =
                          commit.commit?.message?.split("\n")[0] ||
                          "No message";
                        const authorName =
                          commit.commit?.author?.name ||
                          commit.author?.login ||
                          "Unknown";
                        const commitDate =
                          commit.commit?.author?.date ||
                          commit.commit?.committer?.date ||
                          commit.author?.date;
                        const stats = commitDetails[commit.sha];

                        return (
                          <div key={commit.sha} className="updates-commit-item">
                            <div className="updates-commit-main">
                              <div className="updates-commit-message">
                                {message}
                              </div>
                              <div className="updates-commit-meta">
                                <span className="updates-commit-author">
                                  {authorName}
                                </span>
                                <span className="updates-commit-date">
                                  {formatLongDate(commitDate)}
                                </span>
                                <a
                                  href={commit.html_url}
                                  className="updates-commit-link"
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  View on GitHub
                                </a>
                              </div>
                            </div>
                            <div className="updates-commit-footer">
                              <span className="updates-commit-sha">
                                {commit.sha.slice(0, 7)}
                              </span>
                              {stats ? (
                                <div className="updates-commit-stats">
                                  <span className="updates-commit-additions">
                                    +{stats.additions}
                                  </span>
                                  <span className="updates-commit-deletions">
                                    -{stats.deletions}
                                  </span>
                                  <span className="updates-commit-total">
                                    {stats.total} changes
                                  </span>
                                </div>
                              ) : (
                                <span className="updates-commit-stats-muted">
                                  Fetching change stats…
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </Col>
      </Row>
    </Container>
  );
}
